/**
 * As janelas próprias da mesa (docs/spec/popout-windows.md): abrir a janela e o canal entre ela e a
 * janela principal que a abriu.
 *
 * Cada janela própria é uma instância própria do cliente (mesma origem, conexão própria), que desenha
 * um pedaço do layout da mesa, com espaços e abas, como a mesa. O canal (`BroadcastChannel`) só
 * coordena: a janela abre e fecha, e o que ela muda no layout (trocar, fechar, arrastar e dividir as
 * abas e os espaços) e o que ela pede (abrir uma ficha, uma conversa) vão para a principal, que é
 * quem grava o layout.
 *
 * Cada janela tem um id: a marca `out` da raiz do pedaço dela, no layout.
 */
import type { ChatChannel } from '../shared.js';
import { POPOUT } from '../windowMode.js';
import type { LayoutOp } from './layout.js';

export type WindowMessage =
  /** janela própria → principal: estou aberta (ao abrir e ao responder `hello`) */
  | { t: 'here'; mainId: string; roomId: string; windowId: string }
  /** a janela própria está saindo (fechou ou recarregou) */
  | { t: 'closed'; mainId: string; roomId: string; windowId: string }
  /** a janela própria mudou o layout (trocar, fechar, arrastar, dividir): a principal aplica e grava */
  | { t: 'layout'; mainId: string; roomId: string; op: LayoutOp }
  /** a pessoa clicou num espaço de uma janela própria: ele passa a ser o último usado */
  | { t: 'touch'; mainId: string; roomId: string; nodeId: string }
  /**
   * a biblioteca de uma janela própria (`windowId`) pediu para abrir um item: ele abre nessa janela
   * (docs/spec/room-layout.md). Com `popWindow`, ela mesma já abriu outra janela para o item (só quem
   * recebeu o clique pode abrir janelas), e a aba vai para lá
   */
  | { t: 'open-item'; mainId: string; roomId: string; itemId: string; windowId: string; popWindow?: string }
  /** pediram uma conversa privada numa janela própria: ela abre nessa janela */
  | { t: 'open-channel'; mainId: string; roomId: string; channel: ChatChannel; windowId: string }
  /** pediram a conversa com um amigo numa janela própria (perfil → Conversar): abre na principal */
  | { t: 'open-dm'; mainId: string; userId: string }
  /** principal → janelas próprias: carreguei (quem estiver aberta responde `here`) */
  | { t: 'hello'; mainId: string; roomId: string }
  /** feche (sem `windowId`: todas da sala) */
  | { t: 'close'; mainId: string; roomId: string; windowId?: string }
  /** a tabela de macros pediu uma linha no chat principal, que não está na janela dela (room/chatRun.ts) */
  | { t: 'run-chat'; mainId: string; roomId: string; runId: string; text: string }
  /** o chat principal rodou a linha: quem pediu esquece a dele */
  | { t: 'chat-ran'; mainId: string; roomId: string; runId: string }
  /**
   * principal → janelas próprias: o resultado da rolagem já pode aparecer, porque os dados pararam na
   * principal ou porque ela não os anima (docs/spec/dice-3d.md, "Resultado depois da animação")
   */
  | { t: 'roll-shown'; mainId: string; roomId: string; rollId: string }
  | { t: 'focus'; mainId: string; windowId: string };

const channel: BroadcastChannel | null = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('rpgdesk.windows');

/** Id da janela principal: um por aba do navegador (sobrevive a recarregar a página). */
function sessionMainId(): string {
  const fresh = () => `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  try {
    const known = window.sessionStorage.getItem('rpgdesk.mainId');
    if (known) return known;
    const id = fresh();
    window.sessionStorage.setItem('rpgdesk.mainId', id);
    return id;
  } catch {
    return fresh();
  }
}

/** A janela principal desta conversa (na janela própria, a que a abriu). */
export const MAIN_ID: string = POPOUT ? POPOUT.mainId : typeof window === 'undefined' ? 'm0' : sessionMainId();

/** Mensagem sem o `mainId` (o canal põe o desta janela). */
type Outgoing = WindowMessage extends infer M ? (M extends WindowMessage ? Omit<M, 'mainId'> : never) : never;

export function postWindows(message: Outgoing): void {
  channel?.postMessage({ ...message, mainId: MAIN_ID });
}

/** Ouve as mensagens desta janela principal e das janelas próprias dela. */
export function onWindowMessage(fn: (message: WindowMessage) => void): () => void {
  if (!channel) return () => undefined;
  const handler = (e: MessageEvent<WindowMessage>) => {
    if (e.data && typeof e.data === 'object' && e.data.mainId === MAIN_ID) fn(e.data);
  };
  channel.addEventListener('message', handler);
  return () => channel.removeEventListener('message', handler);
}

/** Endereço da janela própria `windowId`. */
export function popoutUrl(windowId: string, roomId: string): string {
  const q = new URLSearchParams({ popout: 'window', win: windowId, room: roomId, main: MAIN_ID });
  return `${window.location.pathname}?${q.toString()}`;
}

/** As janelas que esta principal abriu, pelo id (para focar e para saber se fecharam). */
const opened = new Map<string, Window>();

/** Menor janela própria. */
const MIN_W = 360;
const MIN_H = 300;

/**
 * Abre a janela própria `windowId`, do tamanho e no lugar do espaço (`rect`, coordenadas da página).
 * Chamar dentro do clique: fora dele o bloqueador de pop-ups do navegador barra. Devolve false se a
 * janela não abriu.
 */
export function openPopout(windowId: string, roomId: string, rect?: { left: number; top: number; width: number; height: number } | null): boolean {
  const width = Math.round(Math.max(MIN_W, rect?.width ?? 720));
  const height = Math.round(Math.max(MIN_H, rect?.height ?? 600));
  // a moldura do navegador fica entre a tela e a página
  const chromeTop = Math.max(0, window.outerHeight - window.innerHeight);
  const left = Math.round(window.screenX + (rect?.left ?? 80));
  const top = Math.round(window.screenY + chromeTop + (rect?.top ?? 80));
  const features = `popup=yes,width=${width},height=${height},left=${left},top=${top}`;
  let win: Window | null = null;
  try {
    win = window.open(popoutUrl(windowId, roomId), `rpgdesk-${windowId}`, features);
  } catch {
    win = null;
  }
  if (!win) return false;
  opened.set(windowId, win);
  return true;
}

/** Traz para a frente a janela própria. */
export function focusPopout(windowId: string): void {
  const win = opened.get(windowId);
  if (win && !win.closed) win.focus();
  else postWindows({ t: 'focus', windowId });
}

/**
 * A janela fechou de vez? `true`/`false` quando foi esta principal que a abriu; `null` quando não
 * sabe (a principal recarregou depois de abrir).
 */
export function popoutClosed(windowId: string): boolean | null {
  const win = opened.get(windowId);
  if (!win) return null;
  if (!win.closed) return false;
  opened.delete(windowId);
  return true;
}

/** As janelas (abertas por esta principal) que já fecharam. */
export function closedPopouts(): string[] {
  return [...opened.keys()].filter((id) => popoutClosed(id) === true);
}
